import React from 'react';
import { Info, Thermometer, Wind, Droplets, Home, Sun, Snowflake, Zap } from 'lucide-react';

/**
 * Компонент с информацией о теплопотерях и рекомендациями
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-red-500" />
        Всё о теплопотерях: основы, расчеты, энергоэффективность
      </h2>
      
      <div className="prose prose-red dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Thermometer className="w-5 h-5 text-red-500" />
          Что такое теплопотери здания?
        </h3>
        <p>
          <strong>Теплопотери</strong> — это потери тепловой энергии через ограждающие конструкции здания (стены, окна, крышу, пол) и в результате воздухообмена. Чем выше теплопотери, тем больше энергии требуется для отопления здания и поддержания комфортной температуры внутри помещения.
        </p>
        <p>
          В России вопрос теплопотерь особенно актуален из-за холодного климата большей части страны и продолжительного отопительного сезона, который может длиться от 5 до 9 месяцев в зависимости от региона.
        </p>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-red-500" />
          Почему важно считать теплопотери?
        </h3>
        <ul>
          <li><strong>Экономия на отоплении</strong> — правильно рассчитанная система отопления не будет потреблять лишней энергии, что снизит коммунальные платежи.</li>
          <li><strong>Комфортный микроклимат</strong> — отсутствие сквозняков, равномерное распределение тепла, отсутствие зон переохлаждения.</li>
          <li><strong>Защита от конденсата и плесени</strong> — правильная теплоизоляция исключает образование точек росы и развитие грибка на стенах.</li>
          <li><strong>Экологичность</strong> — снижение расхода топлива для обогрева означает меньший углеродный след.</li>
          <li><strong>Соответствие нормативам</strong> — современные строительные нормы требуют соблюдения определенных показателей энергоэффективности.</li>
        </ul>

        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800 my-6">
          <h4 className="text-red-700 dark:text-red-300 font-medium">Требования по энергоэффективности в России</h4>
          <p className="text-red-600 dark:text-red-400 text-sm mb-0">
            С 2020 года все новые здания в России должны соответствовать классу энергоэффективности не ниже "B" (высокий) согласно СП 50.13330.2012 и Приказу Минстроя №399/пр.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Home className="w-5 h-5 text-red-500" />
          Основные источники теплопотерь
        </h3>
        <ul>
          <li>
            <strong>Стены</strong> — в среднем через стены теряется 20-30% тепла. Качество теплоизоляции и наличие тепловых мостов существенно влияют на этот показатель.
          </li>
          <li>
            <strong>Окна</strong> — могут быть источником до 15-25% всех теплопотерь, особенно старые окна с одинарным остеклением.
          </li>
          <li>
            <strong>Крыша и чердачные перекрытия</strong> — через неутепленную крышу может уходить до 10-25% тепла, так как теплый воздух стремится вверх.
          </li>
          <li>
            <strong>Пол и фундамент</strong> — на их долю приходится около 10-15% теплопотерь, особенно в домах без подвала или с неутепленным цокольным этажом.
          </li>
          <li>
            <strong>Вентиляция и инфильтрация</strong> — неконтролируемый воздухообмен может составлять 25-40% всех теплопотерь здания.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Droplets className="w-5 h-5 text-red-500" />
          Коэффициент теплопередачи (U-value)
        </h3>
        <p>
          Коэффициент теплопередачи (U-value) — ключевой показатель теплоизоляционных свойств строительных конструкций. Он показывает, сколько теплоты проходит через 1 м² конструкции за единицу времени при разнице температур в 1°C. Измеряется в Вт/(м²·°C).
        </p>
        <p>
          Чем <strong>ниже</strong> значение U-value, тем <strong>лучше</strong> теплоизоляционные свойства. Согласно современным российским нормам:
        </p>
        <ul>
          <li>Для стен: 0,15-0,30 Вт/(м²·°C) в зависимости от региона</li>
          <li>Для окон: 0,6-1,5 Вт/(м²·°C)</li>
          <li>Для крыши: 0,15-0,20 Вт/(м²·°C)</li>
          <li>Для пола: 0,20-0,30 Вт/(м²·°C)</li>
        </ul>
        <p>
          Для сравнения: у типичной кирпичной стены толщиной 380 мм без утепления U-value составляет около 1,5-1,8 Вт/(м²·°C), что значительно выше современных требований.
        </p>

        <h3 className="flex items-center gap-2">
          <Sun className="w-5 h-5 text-red-500" />
          Климатические зоны и требования
        </h3>
        <p>
          Россия разделена на несколько климатических зон, для каждой из которых установлены свои требования к теплоизоляции зданий. Ключевым параметром является градусо-сутки отопительного периода (ГСОП) — показатель, учитывающий продолжительность и среднюю температуру отопительного периода.
        </p>
        <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-700 mt-4 mb-6">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-700 p-2 text-sm">Регион</th>
              <th className="border border-gray-300 dark:border-gray-700 p-2 text-sm">ГСОП, °C·сут</th>
              <th className="border border-gray-300 dark:border-gray-700 p-2 text-sm">Расч. темп. наруж. воздуха</th>
              <th className="border border-gray-300 dark:border-gray-700 p-2 text-sm">Отопит. период, дней</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">Москва</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">4551</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">-25°C</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">205</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">Санкт-Петербург</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">4796</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">-24°C</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">220</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">Новосибирск</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">6601</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">-37°C</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">230</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">Краснодар</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">2460</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">-19°C</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">149</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">Якутск</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">10524</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">-52°C</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2 text-sm">252</td>
            </tr>
          </tbody>
        </table>

        <h3 className="flex items-center gap-2">
          <Wind className="w-5 h-5 text-red-500" />
          Вентиляция и воздухообмен
        </h3>
        <p>
          Правильная организация вентиляции — важнейший фактор в балансе между энергоэффективностью и здоровым микроклиматом помещения. В России нормы воздухообмена регламентируются СП 60.13330.2020.
        </p>
        <ul>
          <li><strong>Естественная вентиляция</strong> — самый распространенный тип в российских домах, основанный на разнице давлений и естественной тяге. Недостаток — большие теплопотери в холодное время года.</li>
          <li><strong>Механическая вентиляция</strong> — обеспечивает контролируемый воздухообмен независимо от внешних условий, но требует затрат энергии на работу вентиляторов.</li>
          <li><strong>Вентиляция с рекуперацией тепла</strong> — позволяет сохранить до 85% тепловой энергии при воздухообмене. Набирает популярность в российском энергоэффективном строительстве.</li>
        </ul>
        <p>
          Согласно санитарным нормам, кратность воздухообмена в жилых помещениях должна составлять не менее 0,35-0,5 объема помещения в час. Слишком низкий воздухообмен приводит к накоплению влаги и СО₂, а слишком высокий — к избыточным теплопотерям.
        </p>

        <h3 className="flex items-center gap-2">
          <Snowflake className="w-5 h-5 text-red-500" />
          Современные материалы и технологии теплоизоляции
        </h3>
        <p>
          В условиях российского климата правильно подобранная теплоизоляция играет ключевую роль в снижении теплопотерь:
        </p>
        <ul>
          <li>
            <strong>Минеральная вата</strong> — наиболее распространенный материал в России благодаря оптимальному соотношению цены и качества, негорючести и паропроницаемости. Теплопроводность: 0,035-0,045 Вт/(м·°C).
          </li>
          <li>
            <strong>Пенополистирол (EPS, XPS)</strong> — популярен для утепления фундаментов и плоских крыш благодаря низкому водопоглощению и высокой прочности. Теплопроводность: 0,030-0,040 Вт/(м·°C).
          </li>
          <li>
            <strong>Пенополиуретан (ППУ)</strong> — обеспечивает бесшовную теплоизоляцию и эффективен для сложных поверхностей. Теплопроводность: 0,022-0,032 Вт/(м·°C).
          </li>
          <li>
            <strong>Эковата</strong> — экологичный материал из целлюлозы, обладает хорошей теплоемкостью. Теплопроводность: 0,037-0,042 Вт/(м·°C).
          </li>
          <li>
            <strong>Вакуумные изоляционные панели</strong> — инновационное решение с рекордно низкой теплопроводностью: 0,004-0,008 Вт/(м·°C), но пока мало распространены в России из-за высокой стоимости.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-red-500" />
          Классы энергоэффективности зданий в России
        </h3>
        <p>
          В соответствии с Приказом Минстроя России № 399/пр здания классифицируются по энергоэффективности:
        </p>
        <ul>
          <li><strong>Класс A++</strong> — Высочайший (снижение энергопотребления более 60% от базового уровня)</li>
          <li><strong>Класс A+</strong> — Очень высокий (снижение 50-60%)</li>
          <li><strong>Класс A</strong> — Высокий (снижение 40-50%)</li>
          <li><strong>Класс B</strong> — Повышенный (снижение 30-40%)</li>
          <li><strong>Класс C</strong> — Нормальный (снижение 15-30%)</li>
          <li><strong>Класс D</strong> — Пониженный (снижение 0-15%)</li>
          <li><strong>Класс E</strong> — Низкий (превышение до 25%)</li>
          <li><strong>Класс F</strong> — Очень низкий (превышение 25-50%)</li>
          <li><strong>Класс G</strong> — Крайне низкий (превышение более 50%)</li>
        </ul>
        <p>
          Большинство зданий, построенных до 2000 года, относятся к классам D-G. Современные многоквартирные дома чаще имеют класс C или B. Новые частные дома с качественной теплоизоляцией могут достигать класса A.
        </p>

        <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600 my-6">
          <h4 className="text-gray-900 dark:text-white font-medium">Среднее удельное потребление энергии на отопление:</h4>
          <ul className="text-gray-700 dark:text-gray-300">
            <li>Старый жилой фонд (до 2000 г.) — 200-350 кВт·ч/м² в год</li>
            <li>Современные дома (после 2000 г.) — 100-150 кВт·ч/м² в год</li>
            <li>Энергоэффективные дома — 50-70 кВт·ч/м² в год</li>
            <li>Пассивные дома — менее 15 кВт·ч/м² в год (единичные проекты в России)</li>
          </ul>
        </div>

        <h3>Как использовать данные расчета теплопотерь?</h3>
        <ol>
          <li><strong>Подбор системы отопления</strong> — мощность системы должна превышать расчетные теплопотери на 20-30% для обеспечения запаса и быстрого прогрева.</li>
          <li><strong>Выявление слабых мест</strong> — анализ распределения теплопотерь позволяет понять, какие элементы здания требуют дополнительной теплоизоляции.</li>
          <li><strong>Планирование энергосберегающих мероприятий</strong> — определение экономически эффективных мер по снижению теплопотерь.</li>
          <li><strong>Оценка окупаемости</strong> — сравнение затрат на теплоизоляцию с ожидаемой экономией на отоплении.</li>
        </ol>

        <p className="font-semibold mt-6">
          Наш калькулятор теплопотерь поможет вам точно рассчитать тепловой баланс здания, правильно подобрать систему отопления и повысить энергоэффективность вашего дома!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;